import {Injectable} from '@angular/core';
import {GruppeId, Modelloppdatering, ModelloppdateringType, OpprettUndersøkelseSpesifikasjon, RedigerUndersøkelseSpesifikasjon, SpørreundersøkelseEnkelModell, SpørreundersøkelseId, SpørreundersøkelseModell, SpørreundersøkelseRessursClient, StengForBesvarelseSpesifikasjon, ÅpneForBesvarelseSpesifikasjon} from "../ressurser/generertNg2HttpKlient";
import {firstValueFrom, Observable} from "rxjs";
import {createStore, filterNil} from "@ngneat/elf";
import {deleteEntities, selectEntity, selectManyByPredicate, upsertEntities, withEntities} from "@ngneat/elf-entities";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, Feilhåndteringsstrategi, forelderIdPredikat, opprettEllerOppdatertHendelse, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, slettHendelse, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "./datatjeneste";
import {DataOgMeta, mapTilData} from "./DataOgMeta";
import {HttpResponse} from "@angular/common/http";
import {i18n, Oversettelser} from "../i18n/Oversettelser";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {initDebugger} from "../../støttefunksjoner/debug";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {sorterSpørreundersøkelser} from "felles-ng2/modell/spørreundersøkelseModellhjelpere";
import {tap} from "rxjs/operators";
import {Modelloppdateringer, OppdateringmodellMøteOppdatert, OppdateringmodellMøteSlettet} from "felles-ng2/tjenester/Modelloppdateringer";

const debug = initDebugger('SpørreundersøkelseDatatjeneste');

/** Lager for {@link SpørreundersøkelseModell} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class SpørreundersøkelseDatatjeneste {

    /** Lager for {@link SpørreundersøkelseModell}. Bruker {@link GruppeId} som forelder */
    private spørreundersøkelseLager = createStore({name: 'spørreundersøkelse'}, withEntities<SpørreundersøkelseModell>());

    /** Lager for {@link SpørreundersøkelseEnkelModell}. Bruker {@link GruppeId} som forelder */
    private spørreundersøkelseEnkelLager = createStore({name: 'spørreundersøkelseEnkel'}, withEntities<SpørreundersøkelseEnkelModell>());

    /** Holder rede på åpne subscriptions basert på spørreundersøkelseId. Benyttes ifbm modelloppdateringer. */
    private readonly spørreundersøkelseAbonnenter = new DatalagerAbonnenter('spørreundersøkelse.id');

    constructor(private spørreundersøkelseRessurs: SpørreundersøkelseRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdatering: Modelloppdateringer) {
        this.propagerEndringerTilAndreDatatjenester();
        this.lyttTilModelloppdateringer();
    }


    /** Erstatter angitt spørreundersøkelse i lokalt datalager */

    /* erstattMøteILokallager(spørreundersøkelse: SpørreundersøkelseModell): SpørreundersøkelseModell {
         registrerEntitetMedForelder(this.spørreundersøkelseLager, spørreundersøkelse, this.forelderIdFraMøte(spørreundersøkelse));
         return spørreundersøkelse;
     }
 */

    /** Finner alle undersøkelser knyttet til angitt gruppe, sortert etter responsperiode.start slik at pågående undersøkelser kommer før avsluttede */
    finnTilknyttetGruppeMedMeta(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<SpørreundersøkelseEnkelModell[]>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.spørreundersøkelseRessurs.finnTilknyttetGruppe({id: gruppeId}),
            registrerILager: (respons: HttpResponse<SpørreundersøkelseEnkelModell[]>) => {
                return erstatteAlleEntiteterMedForelder(this.spørreundersøkelseEnkelLager, respons.body!, gruppeId)
            },
            hentFraLager: () => {
                return this.spørreundersøkelseEnkelLager.pipe(
                  selectManyByPredicate(forelderIdPredikat(gruppeId)),
                  sorterLevertSamling(standardSortering));
            },
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n("Kunne ikke hente spørreundersøkelser")), 'finnTilknyttetGruppeMedMeta')
        });
    }

    /** @see #finnTilknyttetGruppeMedMeta */
    finnTilknyttetGruppe(gruppeId: GruppeId, feilhåndteringsstrategi = Feilhåndteringsstrategi.visFeilmelding): Observable<SpørreundersøkelseEnkelModell[]> {
        return this.finnTilknyttetGruppeMedMeta(gruppeId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    hentMedMeta(id: SpørreundersøkelseId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<SpørreundersøkelseModell>> {
        return this.spørreundersøkelseAbonnenter.observer(id,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.spørreundersøkelseRessurs.hent(id),
              //@todo egen commit
              registrerILager: (respons: HttpResponse<SpørreundersøkelseModell>) => {
                  let undersøkelse = respons.body!;
                  return registrerEntitetMedForelder(this.spørreundersøkelseLager, undersøkelse, undersøkelse.eiendeGruppeId);
              },
              hentFraLager: () => this.spørreundersøkelseLager.pipe(selectEntity(id), filterNil()),
              /* hentFraLager: () => this.spørreundersøkelseRessurs.pipe(
                 selectEntity(id),
                 leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                 distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer*/
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente spørreundersøkelse', 'hentMedMeta')
          }));
    }

    hent(id: SpørreundersøkelseId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<SpørreundersøkelseModell> {
        return this.hentMedMeta(id, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    åpneForBesvarelse(id: SpørreundersøkelseId, spesifikasjon: ÅpneForBesvarelseSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SpørreundersøkelseModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.spørreundersøkelseRessurs.åpneForBesvarelse(id, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertUndersøkelse = respons.body!;
                  registrerEntitetMedForelder(this.spørreundersøkelseLager, oppdatertUndersøkelse, oppdatertUndersøkelse.eiendeGruppeId);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke åpne spørreundersøkelsen for besvarelse')), 'åpneForBesvarelse')
        }));
    }

    stengForBesvarelse(id: SpørreundersøkelseId, spesifikasjon: StengForBesvarelseSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SpørreundersøkelseModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.spørreundersøkelseRessurs.stengForBesvarelse(id, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertUndersøkelse = respons.body!;
                  registrerEntitetMedForelder(this.spørreundersøkelseLager, oppdatertUndersøkelse, oppdatertUndersøkelse.eiendeGruppeId);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke stenge spørreundersøkelsen for besvarelse')), 'stengForBesvarelse')
        }));
    }

    opprettUndersøkelse(spesifikasjon: OpprettUndersøkelseSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SpørreundersøkelseModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.spørreundersøkelseRessurs.opprettUndersøkelse(spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const nyUndersøkelse = respons.body!;
                  registrerEntitetMedForelder(this.spørreundersøkelseLager, nyUndersøkelse, nyUndersøkelse.eiendeGruppeId);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere ny spørreundersøkelse')), 'opprettUndersøkelse')
        }));
    }

    oppdaterUndersøkelse(id: SpørreundersøkelseId, spesifikasjon: RedigerUndersøkelseSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<SpørreundersøkelseModell> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.spørreundersøkelseRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  const oppdatertUndersøkelse = respons.body!;
                  registrerEntitetMedForelder(this.spørreundersøkelseLager, oppdatertUndersøkelse, oppdatertUndersøkelse.eiendeGruppeId);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere spørreundersøkelse')), 'oppdaterUndersøkelse')
        }));
    }

    async slett(id: SpørreundersøkelseId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.spørreundersøkelseRessurs.slett(id).råRespons()
              .pipe(tap(respons => this.spørreundersøkelseLager.update(deleteEntities(id)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette spørreundersøkelse')), 'slett')
        }));
    }

    //hjelpemetoder

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(feilhåndteringsstrategi: STRATEGI,
                                                                               brukerrettetFeilmelding: string,
                                                                               metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /** Ved endringer i spørreundersøkelse må lager som håndterer data inkludert i spørreundersøkelse oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.spørreundersøkelseLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer spørreundersøkelseLager ${hendelse.type} til spørreundersøkelseEnkelLager for spørreundersøkelsene ${hendelse.ids}`, hendelse, entiteter);
            entiteter.forEach(spørreundersøkelse => {
                this.spørreundersøkelseEnkelLager.update(upsertEntities(spørreundersøkelse));
            });
        });

        slettHendelse(this.spørreundersøkelseLager).subscribe(hendelse => {
            debug(`propagerer spørreundersøkelseLager ${hendelse.type} til spørreundersøkelseEnkelLager for spørreundersøkelsene ${hendelse.ids}`);
            this.spørreundersøkelseEnkelLager.update(deleteEntities(hendelse.ids));
        });
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        //lytter ikke til ModelloppdateringType.NYTT_MØTE da vi ikke har noe abonnement basert på en 'forelder' (vi har kun abonnement på møte.id,
        this.lyttTilMøteOppdatert();
        this.lyttTilMøteSlettet();
    }

    private lyttTilMøteOppdatert() {
        this.opprettetEllerOppdatertMøteHendelse<OppdateringmodellMøteOppdatert>(ModelloppdateringType.SPØRREUNDERSØKELSE_OPPDATERT)
          .medAbonnentoppslag(this.spørreundersøkelseAbonnenter, møteId => møteId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Møte "{møte}" oppdatert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilMøteSlettet() {
        this.slettetHendelse<OppdateringmodellMøteSlettet>(ModelloppdateringType.SPØRREUNDERSØKELSE_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Møte "{møte}" slettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell)))
          .oppdaterLagerVedHendelse();
    }

    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedSpørreundersøkelse>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, SpørreundersøkelseModell>(this.spørreundersøkelseLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(møteId => møteId);
    }

    private opprettetEllerOppdatertMøteHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellMedSpørreundersøkelse>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, SpørreundersøkelseModell>(this.spørreundersøkelseLager, this.modelloppdatering)
          .lyttTil(hendelseType)
          .medEntitetId(spørreundersøkelseId => spørreundersøkelseId)
          .medEntitetLaster(spørreundersøkelseId => this.spørreundersøkelseRessurs.hent(spørreundersøkelseId).råRespons());
    }

    /** returnerer objekt med 'møte' og 'bruker' felt populert fra angitt oppdatering  */
    private i18nArgumenter(oppdatering: Modelloppdatering<any>, spørreundersøkelseId: SpørreundersøkelseId) {
        return i18nArgumenterMedAnsvarligBruker(oppdatering, {møte: this.undersøkelseTittel(spørreundersøkelseId)});
    }

    /** Returnerer 'spørreundersøkelse.tittel' for undersøkelse registrert i spørreundersøkelseEnkelLager, '...' dersom undersøkelsen ikke finnes i spørreundersøkelseEnkelLager */
    private undersøkelseTittel(spørreundersøkelseId: SpørreundersøkelseId): string {
        const undersøkelse = this.spørreundersøkelseEnkelLager.value.entities[spørreundersøkelseId];
        if (erNil(undersøkelse)) return '...'
        return undersøkelse.tittel;
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }
}

type OppdateringmodellMedSpørreundersøkelse = SpørreundersøkelseId;
const standardSortering = sorterSpørreundersøkelser;
